import React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, BellRing, MessageSquare, Tag, Megaphone, Share2, Coins, ShieldCheck, Hash, LogOut, Zap, Mail, Settings, Users, Truck, TrendingUp, BarChart3, Search } from "lucide-react";
import { PeptigenLogo } from "../components/Logo";
import { useAuth } from "../lib/auth";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/dispatch", label: "Dispatch", icon: Truck },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/attribution", label: "Attribution", icon: TrendingUp },
  { to: "/admin/sales-report", label: "Sales Reports", icon: BarChart3 },
  { to: "/admin/seo", label: "SEO & Organic", icon: Search },
  { to: "/admin/launch-ads", label: "Launch Ads", icon: Zap },
  { to: "/admin/email-activity", label: "Email Activity", icon: Mail },
  { to: "/admin/social", label: "Social", icon: Hash },
  { to: "/admin/affiliates", label: "Affiliates", icon: Share2 },
  { to: "/admin/payouts", label: "Payouts", icon: Coins },
  { to: "/admin/notify", label: "Notify-Me", icon: BellRing },
  { to: "/admin/contact", label: "Messages", icon: MessageSquare },
  { to: "/admin/discounts", label: "Discounts", icon: Tag },
  { to: "/admin/deliverability", label: "Deliverability", icon: ShieldCheck },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPacker = admin?.role === "packer";
  const items = isPacker ? navItems.filter((n) => n.to === "/admin/dispatch") : navItems;

  const handleLogout = () => {
    logout();
    navigate("/staff-portal-72f4");
  };

  if (isPacker && location.pathname !== "/admin/dispatch") {
    return <Navigate to="/admin/dispatch" replace />;
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#080F1B" }} data-testid="admin-layout">
      <aside className="hidden lg:flex w-64 flex-col border-r" style={{ borderColor: "rgba(0,194,214,0.18)", backgroundColor: "#0B1220" }}>
        <div className="p-6 border-b" style={{ borderColor: "rgba(0,194,214,0.18)" }}>
          <Link to="/"><PeptigenLogo size={30} /></Link>
          <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00] mt-3">
            {isPacker ? "Dispatch Desk" : "Lab Control Room"}
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" data-testid="admin-nav">
          {items.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[#00C2D6]/10 text-[#00C2D6] border border-[#00C2D6]/30"
                    : "text-[#9CA8B8] hover:text-[#F7FAFC] hover:bg-[#111A2B] border border-transparent"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase()}`}
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "rgba(0,194,214,0.18)" }}>
          <div className="pg-micro mb-1">Signed in as</div>
          <div className="text-[#F7FAFC] text-sm pg-heading mb-3" style={{ fontFamily: "'Rajdhani'" }} data-testid="admin-signed-in-as">
            {admin?.username || "—"}{isPacker ? <span className="text-[#9CA8B8] text-xs ml-2">· packer</span> : null}
          </div>
          <button onClick={handleLogout} className="pg-btn-ghost w-full text-xs" data-testid="admin-logout">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <header className="lg:hidden flex items-center justify-between p-4 border-b" style={{ borderColor: "rgba(0,194,214,0.18)" }}>
          <PeptigenLogo size={28} />
          <button onClick={handleLogout} className="pg-btn-ghost text-xs"><LogOut size={14} /> Sign Out</button>
        </header>
        <div className="lg:hidden flex overflow-x-auto border-b px-3 py-2 gap-2" style={{ borderColor: "rgba(0,194,214,0.18)" }}>
          {items.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-xs whitespace-nowrap rounded-sm border ${
                  isActive ? "border-[#00C2D6] text-[#00C2D6] bg-[#00C2D6]/10" : "border-[#00C2D6]/20 text-[#9CA8B8]"
                }`
              }
            >
              <Icon size={12} />
              {label}
            </NavLink>
          ))}
        </div>
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
